import type {
  AggregateRequest,
  AggregateResponse,
  GetDataRequest,
  GetSessionsRequest,
} from "@api/api";
import type {
  AssignCompanySeatBody,
  AssignCompanySeatResponse,
  CompanyWithCounts,
  CopyProjectBody,
  CopyProjectResponse,
  CreateCompanyBody,
  CreateCompanyResponse,
  CreateCompanySeatsResponse,
  CreateCreditAllocationBody,
  CreateCreditAllocationResponse,
  CreateUserBody,
  CreateUserResponse,
  DeleteCompanyResponse,
  DeleteCompanySeatResponse,
  DeleteUserResponse,
  DeployDemoBody,
  DeployDemoResponse,
  DisableUserResponse,
  EnableUserResponse,
  LiftAiSuspensionResponse,
  GetAiFeedbackResponse,
  GetEvalRunResponse,
  GetEvalRunsResponse,
  GetCompanySeatsResponse,
  GetCreditAllocationsResponse,
  GetMaintenanceResponse,
  SetMaintenanceBody,
  SetMaintenanceResponse,
  RecalculateCompanyResponse,
  ResendTempPasswordResponse,
  ResetMfaResponse,
  UpdateCompanyBody,
  UpdateCompanyResponse,
  UpdateCompanySeatBody,
  UpdateCompanySeatResponse,
  UserWithCognitoStatus,
  CopyCompany,
  CopyUser,
} from "@api/app-api";
import type { EvalRunData, EvalRunRef } from "@global/types/ai-evals";
import type { AggregatedSessionV2, GroupData } from "@global/types/session";
import { refreshTokens } from "./auth";
import { config, Environment } from "./config";

class ApiClient {
  private environment: Environment;
  private onUnauthorized?: () => void;

  constructor() {
    this.environment = config.defaultEnvironment;
  }

  setEnvironment(env: Environment) {
    this.environment = env;
    localStorage.setItem("selectedEnvironment", env);
  }

  getEnvironment(): Environment {
    return this.environment;
  }

  setUnauthorizedCallback(callback: () => void) {
    this.onUnauthorized = callback;
  }

  private ensureSeatManagementAvailable() {
    if (!config.supportsSeatManagement(this.environment)) {
      throw new Error(
        `Seat management is not available in the ${this.environment} environment yet.`,
      );
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    console.log("Making API request to", endpoint);
    const baseUrl = config.getApiUrl(this.environment);

    // Check if user is authenticated
    const idToken = localStorage.getItem("id_token");
    if (!idToken) {
      if (this.onUnauthorized) {
        this.onUnauthorized();
      }
      throw new Error("No id token found. Please sign in.");
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...options.headers,
    };

    let response: Response;
    try {
      response = await fetch(`${baseUrl}${endpoint}`, {
        ...options,
        headers,
      });
    } catch (error) {
      console.error("API request failed:", error);
      throw new Error(
        "Could not reach the admin API. Check your connection and selected environment.",
      );
    }

    console.log("API response:", response);

    // Handle 401 - try refreshing tokens once
    if (response.status === 401) {
      console.log("Unauthorized, attempting to refresh tokens");
      try {
        await refreshTokens();
        const newIdToken = localStorage.getItem("id_token");

        if (!newIdToken) {
          throw new Error("Failed to refresh token");
        }

        // Retry request with new token
        response = await fetch(`${baseUrl}${endpoint}`, {
          ...options,
          headers: {
            ...headers,
            Authorization: `Bearer ${newIdToken}`,
          },
        });
      } catch (error) {
        console.error("Failed to refresh tokens:", error);
        if (this.onUnauthorized) {
          this.onUnauthorized();
        }
        throw new Error("Session expired. Please sign in again.");
      }
    }

    // Still unauthorized after refresh attempt
    if (response.status === 401) {
      if (this.onUnauthorized) {
        this.onUnauthorized();
      }
      throw new Error("Unauthorized. Please sign in again.");
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message ||
          error.error ||
          `Request failed: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getSessions(
    params?: GetSessionsRequest,
  ): Promise<{ sessions: AggregatedSessionV2[] }> {
    const query = new URLSearchParams();
    if (params?.startDate) query.append("startDate", params.startDate);
    if (params?.endDate) query.append("endDate", params.endDate);
    if (params?.limit) query.append("limit", params.limit.toString());

    const queryString = query.toString();
    return this.request(`/sessions${queryString ? `?${queryString}` : ""}`);
  }

  async getData(params?: GetDataRequest): Promise<Record<string, GroupData>> {
    const query = new URLSearchParams();
    if (params?.startDate) query.append("startDate", params.startDate);
    if (params?.endDate) query.append("endDate", params.endDate);
    if (params?.granularity) query.append("granularity", params.granularity);

    const queryString = query.toString();
    return this.request(`/data${queryString ? `?${queryString}` : ""}`);
  }

  async triggerAggregation(
    params?: AggregateRequest,
  ): Promise<AggregateResponse> {
    return this.request("/aggregate", {
      method: "POST",
      body: JSON.stringify(params || {}),
    });
  }

  async deleteSession(
    sessionId: string,
  ): Promise<{ message: string; sessionId: string }> {
    return this.request(`/sessions/${encodeURIComponent(sessionId)}`, {
      method: "DELETE",
    });
  }

  // ========== App Management API ==========

  async getCompanies(): Promise<CompanyWithCounts[]> {
    return this.request("/app/companies");
  }

  async getAiFeedback(): Promise<GetAiFeedbackResponse> {
    return this.request("/app/ai-feedback");
  }

  async getEvalRuns(): Promise<GetEvalRunsResponse> {
    return this.request("/app/evals");
  }

  /** The run data comes straight from the results bucket through a short-lived link. */
  async getEvalRun(ref: EvalRunRef): Promise<EvalRunData> {
    const { url } = await this.request<GetEvalRunResponse>(`/app/evals/${ref.kind}/${encodeURIComponent(ref.id)}`);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Run data could not be loaded (${response.status})`);
    return response.json();
  }

  async getMaintenance(): Promise<GetMaintenanceResponse> {
    return this.request("/app/maintenance");
  }

  async setMaintenance(
    state: SetMaintenanceBody,
  ): Promise<SetMaintenanceResponse> {
    return this.request("/app/maintenance", {
      method: "PUT",
      body: JSON.stringify(state),
    });
  }

  async getCompanyUsers(companyId: string): Promise<UserWithCognitoStatus[]> {
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/users`,
    );
  }

  async getCompanySeats(companyId: string): Promise<GetCompanySeatsResponse> {
    this.ensureSeatManagementAvailable();
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/seats`,
    );
  }

  async addCompanySeat(companyId: string): Promise<CreateCompanySeatsResponse> {
    return this.addCompanySeats(companyId, 1, false);
  }

  async addCompanySeats(
    companyId: string,
    count: number,
    isFlexible: boolean,
  ): Promise<CreateCompanySeatsResponse> {
    this.ensureSeatManagementAvailable();
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/seats`,
      {
        method: "POST",
        body: JSON.stringify({ count, isFlexible }),
      },
    );
  }

  async assignCompanySeat(
    companyId: string,
    seatId: string,
    userId: AssignCompanySeatBody["userId"],
  ): Promise<AssignCompanySeatResponse> {
    this.ensureSeatManagementAvailable();
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/seats/${encodeURIComponent(seatId)}/assign`,
      {
        method: "POST",
        body: JSON.stringify({ userId }),
      },
    );
  }

  async updateCompanySeat(
    companyId: string,
    seatId: string,
    settings: UpdateCompanySeatBody,
  ): Promise<UpdateCompanySeatResponse> {
    this.ensureSeatManagementAvailable();
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/seats/${encodeURIComponent(seatId)}`,
      {
        method: "PATCH",
        body: JSON.stringify(settings),
      },
    );
  }

  async deleteCompanySeat(
    companyId: string,
    seatId: string,
  ): Promise<DeleteCompanySeatResponse> {
    this.ensureSeatManagementAvailable();
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/seats/${encodeURIComponent(seatId)}`,
      {
        method: "DELETE",
      },
    );
  }

  // ========== Credit Allocations ==========

  async getCreditAllocations(
    companyId: string,
  ): Promise<GetCreditAllocationsResponse> {
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/credits`,
    );
  }

  async createCreditAllocation(
    companyId: string,
    data: CreateCreditAllocationBody,
  ): Promise<CreateCreditAllocationResponse> {
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/credits`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }

  async createUser(
    companyId: string,
    userData: CreateUserBody,
  ): Promise<CreateUserResponse> {
    if (userData.seat) this.ensureSeatManagementAvailable();

    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/users`,
      {
        method: "POST",
        body: JSON.stringify(userData),
      },
    );
  }

  async disableUser(
    userId: string,
    companyId: string,
  ): Promise<DisableUserResponse> {
    return this.request(`/app/users/${encodeURIComponent(userId)}/disable`, {
      method: "POST",
      body: JSON.stringify({ userId, companyId }),
    });
  }

  async enableUser(
    userId: string,
    companyId: string,
  ): Promise<EnableUserResponse> {
    return this.request(`/app/users/${encodeURIComponent(userId)}/enable`, {
      method: "POST",
      body: JSON.stringify({ userId, companyId }),
    });
  }

  async liftAiSuspension(
    userId: string,
    companyId: string,
  ): Promise<LiftAiSuspensionResponse> {
    return this.request(
      `/app/users/${encodeURIComponent(userId)}/lift-ai-suspension`,
      {
        method: "POST",
        body: JSON.stringify({ userId, companyId }),
      },
    );
  }

  async deleteUser(
    userId: string,
    companyId: string,
  ): Promise<DeleteUserResponse> {
    return this.request(`/app/users/${encodeURIComponent(userId)}`, {
      method: "DELETE",
      body: JSON.stringify({ userId, companyId }),
    });
  }

  async resetMfa(userId: string, companyId: string): Promise<ResetMfaResponse> {
    return this.request(`/app/users/${encodeURIComponent(userId)}/reset-mfa`, {
      method: "POST",
      body: JSON.stringify({ userId, companyId }),
    });
  }

  async resendTempPassword(
    userId: string,
    companyId: string,
  ): Promise<ResendTempPasswordResponse> {
    return this.request(
      `/app/users/${encodeURIComponent(userId)}/resend-temp-password`,
      {
        method: "POST",
        body: JSON.stringify({ userId, companyId }),
      },
    );
  }

  async createCompany(
    companyData: CreateCompanyBody,
  ): Promise<CreateCompanyResponse> {
    return this.request("/app/companies", {
      method: "POST",
      body: JSON.stringify(companyData),
    });
  }

  async updateCompany(
    companyId: string,
    updates: UpdateCompanyBody,
  ): Promise<UpdateCompanyResponse> {
    return this.request(`/app/companies/${encodeURIComponent(companyId)}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteCompany(companyId: string): Promise<DeleteCompanyResponse> {
    return this.request(`/app/companies/${encodeURIComponent(companyId)}`, {
      method: "DELETE",
    });
  }

  // ========== Recalculation API ==========

  /** Trigger a recalculation of every project + portfolio of a company. */
  async recalculateCompany(
    companyId: string,
  ): Promise<RecalculateCompanyResponse> {
    return this.request(
      `/app/companies/${encodeURIComponent(companyId)}/recalculate`,
      { method: "POST" },
    );
  }

  /** POST to a route outside the environment prefix, for endpoints that are not tied to one environment. */
  private async postAtRoot<T>(endpoint: string, body: unknown): Promise<T> {
    return this.atRoot("POST", endpoint, body);
  }

  private async atRoot<T>(
    method: "GET" | "POST",
    endpoint: string,
    body?: unknown,
  ): Promise<T> {
    const baseUrl = config.getApiUrl(this.environment);
    const rootUrl = baseUrl.replace(/\/(dev|staging|prod)$/, "");

    const idToken = localStorage.getItem("id_token");
    if (!idToken) {
      throw new Error("No id token found. Please sign in.");
    }

    const response = await fetch(`${rootUrl}${endpoint}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message ||
          error.error ||
          `Request failed: ${response.statusText}`,
      );
    }

    return response.json();
  }

  /** Environment-agnostic: source and target environments come from the request body. */
  async copyProject(data: CopyProjectBody): Promise<CopyProjectResponse> {
    return this.postAtRoot("/app/projects/copy", data);
  }

  /** The copy dialog's pickers; `env` may be another environment than the selected one (prod's portal only). */
  async getCopyCompanies(env: Environment): Promise<CopyCompany[]> {
    return this.atRoot("GET", `/app/copy/companies?env=${env}`);
  }

  async getCopyUsers(env: Environment, companyId: string): Promise<CopyUser[]> {
    return this.atRoot(
      "GET",
      `/app/copy/companies/${encodeURIComponent(companyId)}/users?env=${env}`,
    );
  }

  /** Environment-agnostic: the target environment comes from the request body. */
  async deployDemo(data: DeployDemoBody): Promise<DeployDemoResponse> {
    return this.postAtRoot("/app/demo/deploy", data);
  }
}

export const apiClient = new ApiClient();
